import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { DossierDtoEchaResponseStatusEnum, ProductNodeDto, ProductNodeDtoEchaSvhcRelevanceEnum, SubstanceDTO } from '@scip/api';
import { ProductTypeEnum, ProductUtils, SubmissionStatus, translationKeys } from '../../../../../core';
import { ProductsService } from '../../../../../services';
import { TreeNode } from 'primeng/api';
import { AvanoLoadingIndicatorService } from '@avano/avano-components';
import { DossierError } from '../../../../../shared';

@Component({
  selector: 'scip-product-declaration-tree',
  templateUrl: './product-declaration-tree.component.html',
  styleUrls: ['./product-declaration-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductDeclarationTreeComponent implements AfterViewInit {
  @Input() set tabIdentifier(val: number) {
    if (val) {
      this._tabIdentifier = val;
      this.loadRootNode();
    }
  } // we only need the identifier to load the relevant parts for the primeng tree

  @Output() readonly nodeIdentifierSelected: EventEmitter<string> = new EventEmitter<string>();

  bom: TreeNode[] = [];
  selectedNode: TreeNode;
  blockingIdentifier = 'productDeclarationTree';
  rootNodeWasEmpty = false; // if the tree somehow cannot be retrieved from the backend

  translationKeys: typeof translationKeys = translationKeys;
  productType: typeof ProductTypeEnum = ProductTypeEnum;
  echaSvhcRelevance: typeof ProductNodeDtoEchaSvhcRelevanceEnum = ProductNodeDtoEchaSvhcRelevanceEnum;

  submissionStatus: typeof SubmissionStatus = SubmissionStatus;
  showDossierErrorDetails = false;
  selectedDossierError: DossierError;

  private _tabIdentifier: number;

  constructor(
    private productService: ProductsService,
    private changeDet: ChangeDetectorRef,
    private avanoLoadingIndicatorService: AvanoLoadingIndicatorService
  ) {}

  ngAfterViewInit(): void {
    this.avanoLoadingIndicatorService.block([this.blockingIdentifier]);
  }

  nodeSelect(event): void {
    this.nodeIdentifierSelected.emit(this.selectedNode.data.selectionIdentifier);
  }

  lazyLoadNode(event): void {
    // Only if the node was not already loaded before we do the backend call to get the children-productNodeDTOs
    // Reloading everytime the children would cause some strange side-effects:
    // How do we tell the end-user that the productNode changed? We need to update the view in the tree, but also in the editor
    // How do we treat the changes the user already made on that node? How do we merge a meantime updated node with
    // the draft-editing of the user? What wins? What happens if that node we are currently expanding was removed in the
    // meantime? So before "reloading" on every expand there are a lot of questions to be answered ;) and a lot
    // of ui/ux-thoughts needs to be tackled
    if (!event.node.data.childrenLoaded) {
      this.avanoLoadingIndicatorService.block([this.blockingIdentifier]);
      const params = {
        id: this._tabIdentifier,
        resourceIdentifier: event.node.data.identifier,
        orderId: event.node.data.orderId
      };
      this.productService.retrieveProductNodes(params.id, params.resourceIdentifier, params.orderId).subscribe(
        (value) => {
          const nodes: TreeNode[] = this.convertData(value);
          // Add the children (ProductNode-TreeNodes) to the perhaps already added substance-children.
          event.node.children = [...event.node.children, ...nodes];
          event.node.data.childrenLoaded = true; //Mark the node as loaded, so we do not lazyLoad again
          this.bom = [...this.bom];
          this.avanoLoadingIndicatorService.unblock([this.blockingIdentifier]);
          this.changeDet.markForCheck();
        },
        () => {
          // The error message for the user is within the service, we here only need to handle it regarding the tree-behavior
          this.avanoLoadingIndicatorService.unblock([this.blockingIdentifier]);
        }
      );
    }
  }

  showErrorDetails(echaResponseStatus: DossierDtoEchaResponseStatusEnum, echaResponseErrorText: string): void {
    this.selectedDossierError = {
      echaResponseErrorText,
      echaResponseStatus
    };
    this.showDossierErrorDetails = true;
  }

  private loadRootNode(): void {
    // Get possible values for Product Identifier.
    const params = {
      id: this._tabIdentifier,
      resourceIdentifier: null
    };

    this.productService.retrieveProductNodes(params.id, params.resourceIdentifier, null, true).subscribe(
      (value) => {
        const topLevelNode: ProductNodeDto[] = [];
        // We know on the root level that there is only one productNodeDto, so directly use the "first" (and only) one
        if (value?.length) {
          topLevelNode.push(value[0]);
          const productTree: TreeNode[] = this.convertData(topLevelNode, true);
          this.bom = [...productTree];
          this.autoselectRootNode();
        } else {
          this.rootNodeWasEmpty = true; // We enable a message to tell the user that the loading did worked, but the content was empty?
          this.changeDet.markForCheck();
        }
        this.avanoLoadingIndicatorService.unblock([this.blockingIdentifier]);
      },
      (error) => {
        this.avanoLoadingIndicatorService.unblock([this.blockingIdentifier]);
      }
    );
  }

  private autoselectRootNode() {
    this.selectedNode = this.bom[0];
    this.nodeIdentifierSelected.emit(this.selectedNode.data.selectionIdentifier);
  }

  private convertData(nodeToConvert: ProductNodeDto[], firstLevel: boolean = false): TreeNode[] {
    const allProductTreeNodes: TreeNode[] = [];
    for (const productNode of nodeToConvert) {
      if (productNode !== null && productNode !== undefined && productNode !== {}) {
        allProductTreeNodes.push(this.generateProductTreeNode(productNode, firstLevel));
      }
    }
    return allProductTreeNodes;
  }

  private generateProductTreeNode(productNode: ProductNodeDto, firstLevel: boolean): TreeNode {
    const isChildWithDossier = productNode.dossier !== undefined && !firstLevel;

    const substanceChildren: TreeNode[] = [];
    if (productNode.substances?.length) {
      for (const singleSubstance of productNode.substances) {
        if (singleSubstance && singleSubstance !== '') {
          substanceChildren.push(this.generateSubstanceTreeNode(singleSubstance, productNode));
        }
      }
    }
    return {
      data: {
        identifier: productNode.identifier,
        name: productNode.name,
        productType: productNode.productType,
        isDummy: productNode.dummyNode,
        isSVHC: productNode.echaSvhcRelevance,
        id: Math.random(),
        primaryArticleIdentifierValue: productNode.primaryArticleIdentifierValue,
        count: '' + productNode.numberOfInstances,
        orderId: productNode.orderId,
        selectionIdentifier: ProductUtils.getProductNodeDtoIdentifier(productNode),
        isOwn: isChildWithDossier,
        dossierStatus: isChildWithDossier ? productNode.dossier.status : undefined,
        echaResponseStatus: isChildWithDossier ? productNode.dossier.echaResponseStatus : undefined,
        echaResponseErrorText: isChildWithDossier ? productNode.dossier.echaResponseErrorText : undefined
      },
      children: substanceChildren,
      expanded: false,
      leaf: false
    };
  }

  private generateSubstanceTreeNode(substance: SubstanceDTO, parentNode: ProductNodeDto): TreeNode {
    return {
      data: {
        identifier: substance.casNumber,
        name: substance.name,
        productType: ProductTypeEnum.SUBSTANCE,
        isSVHC: substance.echaSvhcRelevance,
        id: Math.random(),
        primaryArticleIdentifierValue: substance.casNumber,
        count: '',
        orderId: parentNode.orderId,
        selectionIdentifier: ProductUtils.getSubstanceNodeIdentifier(substance, parentNode)
      },
      children: null,
      expanded: false,
      leaf: true
    };
  }
}
